import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import { Progress } from "./ui/progress";
import { api, fileUrl } from "../lib/apiClient";
import { normalizeBlueprint } from "../lib/blueprint";
import { useFakeProgress } from "../lib/useFakeProgress";
import { toast } from "sonner";

const PROMPTS = [
  "Modern 3-bedroom minimalist ranch with open kitchen and solar deck",
  "Cozy 2-bedroom Scandinavian cabin with loft and sauna",
  "Luxury 4-bed Mediterranean villa with courtyard and pool house",
];

export default function AIGeneratorModal({ open, onOpenChange, onApply, city }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { progress, elapsed, est, start, done, reset } = useFakeProgress();

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the house you want"); return; }
    setLoading(true); setResult(null); start(85);
    try {
      const res = await api.post("/ai/generate-design", { prompt, city, with_render: true });
      setResult(res.data);
      done();
      toast.success("Design generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
      reset();
    } finally { setLoading(false); }
  };

  const apply = () => {
    const bp = normalizeBlueprint(result);
    onApply(bp, result);
    onOpenChange(false);
    setResult(null); setPrompt("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <Wand2 className="w-5 h-5 text-[#A855F7]" /> AI House Generator
          </DialogTitle>
          <DialogDescription className="text-[#8B949E]">Describe a home and Gemini AI drafts a full floor plan, specs and a render.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            data-testid="ai-prompt-input"
            value={prompt} onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe your dream house — style, bedrooms, features..."
            className="bg-[#0D1117] border-[#30363D] min-h-[90px] font-mono-cad text-sm"
          />
          <div className="flex flex-wrap gap-2">
            {PROMPTS.map((p) => (
              <button key={p} onClick={() => setPrompt(p)}
                className="text-xs px-3 py-1.5 rounded-full border border-[#30363D] text-[#8B949E] hover:border-[#38BDF8] hover:text-[#38BDF8] transition-colors">
                {p.slice(0, 42)}…
              </button>
            ))}
          </div>
          <Button data-testid="ai-generate-submit-btn" onClick={generate} disabled={loading}
            className="w-full bg-[#A855F7] hover:bg-[#9333EA] text-white font-semibold">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Designing your home…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Blueprint</>}
          </Button>

          {loading && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2 bg-[#0D1117]" />
              <div className="flex justify-between text-xs text-[#8B949E] font-mono-cad">
                <span>Drafting floor plan + render… {Math.round(progress)}%</span>
                <span>{elapsed.toFixed(0)}s / ~{est}s</span>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-[#30363D] bg-[#0D1117] p-4 space-y-3 animate-fade-up">
              <div className="flex gap-4">
                {result.render_path && (
                  <img src={fileUrl(result.render_path)} alt="render" className="w-40 h-28 object-cover rounded-md border border-[#30363D]" />
                )}
                <div className="flex-1">
                  <h4 className="font-heading font-semibold">{result.name}</h4>
                  <p className="text-xs text-[#8B949E] mt-1 leading-relaxed">{result.specs}</p>
                </div>
              </div>
              <div className="flex gap-3 text-xs font-mono-cad text-[#8B949E]">
                <span>{result.rooms?.length || 0} rooms</span>
                <span>·</span>
                <span>{result.bedrooms || 0} bed / {result.bathrooms || 0} bath</span>
                <span>·</span>
                <span>{result.total_area_sqft || 0} ft²</span>
              </div>
              <Button data-testid="ai-apply-btn" onClick={apply} className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">
                Load onto Canvas
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
